import React, { useState, useEffect, useCallback } from 'react';
import './StatusTab.css';
import './HealthHourTab.css';
import { MetricCard } from './HealthCards';
import { BC_INDICATORS, STATUS_COLORS, fmtMeasuredAt } from './bcIndicators';

const findStatus = (key, v) => {
  const ind = BC_INDICATORS.find((i) => i.key === key);
  return ind?.status && v != null ? ind.status(v) : null;
};

// Quebra o peso em água/gordura/proteína/osso (Peso = Água + Gordura +
// Proteína + Osso), cada um com a mesma classificação (Saudável/Alto/Baixo)
// que já usamos pros indicadores em %. A barra é proporcional ao peso total
// — reflete a composição real, sem inventar uma escala fixa por item. Mesmo
// estilo de barra horizontal do card Atividades (hm-bar-*).
export function CompositionBreakdown({ cur }) {
  if (!cur?.weight_kg) return null;

  const proteinKg = cur.protein_pct != null
    ? Number((cur.weight_kg * cur.protein_pct / 100).toFixed(1))
    : null;

  const rows = [
    { label: 'Peso da água', icon: '💧', kg: cur.water_kg, status: findStatus('water_pct', cur.water_pct) },
    { label: 'Gordura', icon: '🧈', kg: cur.fat_kg, status: findStatus('fat_pct', cur.fat_pct) },
    { label: 'Proteína', icon: '🥚', kg: proteinKg, status: findStatus('protein_pct', cur.protein_pct) },
    { label: 'Massa óssea', icon: '🦴', kg: cur.bone_kg, status: cur.bone_kg != null ? ['Padrão', 'good'] : null },
  ].filter((r) => r.kg != null);

  if (rows.length === 0) return null;

  return (
    <div className="st-card bc-analysis-card">
      <div className="bc-analysis-head">
        <h3 className="st-title">🧍 Análise da composição corporal</h3>
      </div>
      <span className="bc-analysis-sub">Peso = Água + Gordura + Proteína + Osso</span>
      <span className="bc-analysis-total">{cur.weight_kg}<small>kg</small></span>

      <div className="hm-bars">
        {rows.map((r) => {
          const pct = Math.min(100, (r.kg / cur.weight_kg) * 100);
          const color = STATUS_COLORS[r.status?.[1]] || '#38bdf8';
          return (
            <div className="hm-bar-row" key={r.label}>
              <div className="hm-bar-head">
                <span className="hm-bar-label">{r.icon} {r.label}</span>
                <span className="hm-bar-goal">{r.kg}<small>kg</small></span>
              </div>
              <div className="hm-bar-track">
                <div className="hm-bar-fill" style={{ width: `${pct}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>
      {cur.measured_at && <span className="bc-hero-date">{fmtMeasuredAt(cur.measured_at)}</span>}
    </div>
  );
}

function BodyCompositionTab({ svc }) {
  const [data, setData] = useState(null);
  const [bodyComp, setBodyComp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [d, bc] = await Promise.all([
        svc.getHealthAll(),
        svc.getBodyComposition(1).catch(() => null),
      ]);
      if (d.error) throw new Error(d.error);
      setData(d);
      setBodyComp(bc?.records?.[0] || null);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [svc]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="st-view"><p className="st-msg">Carregando composição corporal…</p></div>;

  return (
    <div className="st-view">
      <div className="st-head">
        <span className="st-updated">{data?.body_ultima_atualizacao ? `Última atualização · ${fmtMeasuredAt(data.body_ultima_atualizacao)}` : ''}</span>
        <button className="analise-refresh" onClick={load} disabled={loading}>{loading ? '…' : 'Atualizar'}</button>
      </div>
      {error && <p className="st-msg st-err">{error}</p>}

      <CompositionBreakdown cur={bodyComp} />

      {data?.body?.length > 0 ? (
        <div className="st-card">
          <h3 className="st-title">🧍 Composição corporal</h3>
          <div className="hh-grid">
            {data.body.map((m) => <MetricCard key={m.key} m={m} />)}
          </div>
        </div>
      ) : (
        !loading && !bodyComp && <p className="st-msg">Sem medições de composição corporal ainda.</p>
      )}

      <div className="st-spacer" />
    </div>
  );
}

export default BodyCompositionTab;
